import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Save, User2, Building2, X, Search, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../services/api";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const DEDUCTOR_TYPES = [
  "Company", "Government", "Firm", "Individual / HUF", "AOP / BOI / Trust",
  "Local Authority", "Artificial Juridical Person",
];

const TAB_DEDUCTOR = "deductor";
const TAB_RP = "rp";

function empDisplayName(emp) {
  const u = emp?.user || {};
  const fromUser = [u.name, u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  if (fromUser) return fromUser;
  return emp?.name || emp?.empCode || `Employee #${emp?.id || ""}`;
}

export default function DeductorMasterModal({
  open,
  onClose,
  darkMode = false,
  filingId,
  employees = [],
}) {
  const [tab, setTab] = useState(TAB_DEDUCTOR);
  const [form, setForm] = useState({});
  const [companyDefaults, setCompanyDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [empSearch, setEmpSearch] = useState("");

  const card = darkMode
    ? "rounded-xl border border-[#3C3C3C] bg-[#242424]"
    : "rounded-xl border border-gray-200 bg-white shadow-sm";
  const inputCls = `mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
    darkMode ? "border-[#555] bg-[#2a2a2a] text-white" : "border-gray-300 bg-white"
  }`;
  const labelCls = "block text-xs font-medium";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollOpsService.getDeductorMaster();
      const payload = res?.data || res || {};
      const master = payload.master || {};
      const defaults = payload.companyDefaults || {};
      setCompanyDefaults(defaults);
      setForm({ ...defaults, ...master });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load deductor master");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      setTab(TAB_DEDUCTOR);
      load();
    }
  }, [open, load]);

  const onChange = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const applyCompanyDefaults = () => {
    if (!companyDefaults || !Object.keys(companyDefaults).length) {
      toast.error("No enterprise company details found");
      return;
    }
    setForm((p) => ({
      ...p,
      deductorName: companyDefaults.deductorName || p.deductorName || "",
      tan: companyDefaults.tan || p.tan || "",
      pan: companyDefaults.pan || p.pan || "",
      flatDoorBlock: companyDefaults.flatDoorBlock || p.flatDoorBlock || "",
      emailPrimary: companyDefaults.emailPrimary || p.emailPrimary || "",
      phonePrimary: companyDefaults.phonePrimary || p.phonePrimary || "",
      deductorType: companyDefaults.deductorType || p.deductorType || "Company",
    }));
    toast.success("Loaded from enterprise company details");
  };

  const handlePrefillEmployee = async (empId) => {
    setPrefilling(true);
    try {
      const res = await payrollOpsService.prefillResponsiblePerson(empId);
      const d = res?.data || res || {};
      if (!d.rpEmployeeId && !d.rpName) {
        throw new Error(res?.message || "No profile data returned");
      }
      setForm((p) => ({
        ...p,
        rpSource: "employee",
        rpEmployeeId: d.rpEmployeeId,
        rpName: d.rpName || "",
        rpDesignation: d.rpDesignation || "",
        rpFatherName: d.rpFatherName || "",
        rpPan: d.rpPan || "",
        rpGender: d.rpGender || "",
        rpMobile: d.rpMobile || "",
        rpPhonePrimary: d.rpPhonePrimary || d.rpMobile || "",
        rpEmailPrimary: d.rpEmailPrimary || "",
        rpFlatDoorBlock: d.rpFlatDoorBlock || "",
        rpCity: d.rpCity || "",
        rpState: d.rpState || "",
        rpPincode: d.rpPincode || "",
      }));
      toast.success("Prefilled from employee profile");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to prefill employee");
    }
    setPrefilling(false);
  };

  const handleSave = async (scope) => {
    setSaving(true);
    try {
      await payrollOpsService.updateDeductorMaster({
        ...form,
        ...(filingId ? { filingId } : {}),
      });
      if (scope === "quarter" && filingId) {
        await payrollOpsService.saveDeductorQuarterOverride(filingId, {
          deductor: form,
          responsiblePerson: form,
        });
        toast.success("Saved for this quarter");
      } else {
        toast.success("Saved for all quarters (master updated)");
      }
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed");
    }
    setSaving(false);
  };

  if (!open) return null;
  if (typeof document === "undefined") return null;

  const filteredEmps = employees.filter((e) => {
    if (!empSearch) return true;
    const s = empSearch.toLowerCase();
    const name = empDisplayName(e).toLowerCase();
    return name.includes(s) || String(e.empCode || "").toLowerCase().includes(s);
  });

  return createPortal(
    <div
      className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/50 p-4"
      onClick={() => onClose(false)}
    >
      <div
        className={`relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl p-6 ${
          darkMode ? "bg-[#1e1e1e] text-white" : "bg-white"
        } shadow-xl`}
        role="dialog"
        aria-modal="true"
        aria-label="Deductor and Responsible Person"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => onClose(false)}
          className="absolute right-4 top-4 rounded-lg p-1 hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          <X size={18} />
        </button>

        <h2 className="mb-4 text-base font-bold">Deductor & Responsible Person</h2>

        <div className="mb-4 flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-[#333]">
          {[
            { key: TAB_DEDUCTOR, icon: Building2, label: "Deductor Details" },
            { key: TAB_RP, icon: User2, label: "Responsible Person" },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-xs font-semibold transition ${
                tab === t.key
                  ? "bg-white shadow-sm dark:bg-[#2a2a2a]"
                  : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              }`}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : tab === TAB_DEDUCTOR ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-gray-500">
                Prefills from Enterprise company details (name, TAN, PAN, address, email, phone).
              </p>
              <button
                type="button"
                onClick={applyCompanyDefaults}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                  darkMode ? "border-[#555] hover:bg-[#333]" : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                <RefreshCw size={12} />
                Load from Enterprise
              </button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Deductor Name *" name="deductorName" value={form.deductorName} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="TAN *" name="tan" value={form.tan} onChange={onChange} inputCls={inputCls} labelCls={labelCls} placeholder="BLRM12345E" />
              <Field label="PAN" name="pan" value={form.pan} onChange={onChange} inputCls={inputCls} labelCls={labelCls} placeholder="ABCDE1234F" />
              <label className={labelCls}>
                Deductor Type
                <select value={form.deductorType || "Company"} onChange={(e) => onChange("deductorType", e.target.value)} className={inputCls}>
                  {DEDUCTOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <Field label="Branch / Division" name="branchDivision" value={form.branchDivision} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="GSTIN" name="gstin" value={form.gstin} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
            </div>

            <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Address</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Flat / Door / Block" name="flatDoorBlock" value={form.flatDoorBlock} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Building Name" name="buildingName" value={form.buildingName} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Street / Road / Lane" name="streetRoad" value={form.streetRoad} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Area / Locality" name="area" value={form.area} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="City / Town" name="city" value={form.city} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <label className={labelCls}>
                State *
                <select value={form.state || ""} onChange={(e) => onChange("state", e.target.value)} className={inputCls}>
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <Field label="Pincode *" name="pincode" value={form.pincode} onChange={onChange} inputCls={inputCls} labelCls={labelCls} placeholder="560001" />
              <label className={`${labelCls} flex items-center gap-2 pt-5`}>
                <input type="checkbox" checked={!!form.addressChanged} onChange={(e) => onChange("addressChanged", e.target.checked)} />
                Address changed since last return
              </label>
            </div>

            <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Contact</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="STD Code" name="phoneStdCode" value={form.phoneStdCode} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Phone (Primary)" name="phonePrimary" value={form.phonePrimary} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Phone (Alternate)" name="phoneAlternate" value={form.phoneAlternate} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Fax" name="fax" value={form.fax} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Email (Primary)" name="emailPrimary" value={form.emailPrimary} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Email (Alternate)" name="emailAlternate" value={form.emailAlternate} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
            </div>

            {(form.deductorType || "").toLowerCase().includes("government") && (
              <>
                <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Government Details</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Field label="AIN" name="govtAin" value={form.govtAin} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
                  <Field label="PAO Code" name="govtPaoCode" value={form.govtPaoCode} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
                  <Field label="PAO Registration" name="govtPaoRegn" value={form.govtPaoRegn} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
                  <Field label="DDO Code" name="govtDdoCode" value={form.govtDdoCode} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
                  <Field label="DDO Registration" name="govtDdoRegn" value={form.govtDdoRegn} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
                  <Field label="Ministry / Dept" name="govtMinistry" value={form.govtMinistry} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-xs font-medium">Source:</label>
              {["employee", "external"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onChange("rpSource", s)}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                    form.rpSource === s
                      ? "border-redhalo-red bg-redhalo-red/10 text-redhalo-red"
                      : darkMode ? "border-[#555]" : "border-gray-300"
                  }`}
                >
                  {s === "employee" ? "Select Employee" : "External Person"}
                </button>
              ))}
              {prefilling && <Loader2 size={14} className="animate-spin text-gray-400" />}
            </div>

            {form.rpSource === "employee" && (
              <div className={`${card} p-3`}>
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
                  <input
                    value={empSearch}
                    onChange={(e) => setEmpSearch(e.target.value)}
                    className={`${inputCls} !pl-8`}
                    placeholder="Search employee by name or code..."
                  />
                </div>
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {filteredEmps.slice(0, 30).map((emp) => (
                    <button
                      key={emp.id}
                      type="button"
                      disabled={prefilling}
                      onClick={() => handlePrefillEmployee(emp.id)}
                      className={`w-full rounded-lg px-3 py-2 text-left text-xs hover:bg-gray-100 dark:hover:bg-[#333] ${
                        Number(form.rpEmployeeId) === Number(emp.id) ? "bg-redhalo-red/10 font-semibold" : ""
                      }`}
                    >
                      <span className="font-medium">{empDisplayName(emp)}</span>
                      <span className="ml-2 text-gray-500">{emp.empCode}</span>
                    </button>
                  ))}
                  {filteredEmps.length === 0 && (
                    <p className="py-2 text-center text-xs text-gray-400">No employees found</p>
                  )}
                </div>
              </div>
            )}

            <p className="pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Person Details</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Name *" name="rpName" value={form.rpName} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Designation *" name="rpDesignation" value={form.rpDesignation} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Father's Name" name="rpFatherName" value={form.rpFatherName} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="PAN" name="rpPan" value={form.rpPan} onChange={onChange} inputCls={inputCls} labelCls={labelCls} placeholder="ABCDE1234F" />
              <label className={labelCls}>
                Gender
                <select value={form.rpGender || ""} onChange={(e) => onChange("rpGender", e.target.value)} className={inputCls}>
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </label>
              <Field label="Mobile" name="rpMobile" value={form.rpMobile} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
            </div>

            <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Address</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Flat / Door / Block" name="rpFlatDoorBlock" value={form.rpFlatDoorBlock} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Building Name" name="rpBuildingName" value={form.rpBuildingName} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Street / Road / Lane" name="rpStreetRoad" value={form.rpStreetRoad} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Area / Locality" name="rpArea" value={form.rpArea} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="City / Town" name="rpCity" value={form.rpCity} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <label className={labelCls}>
                State
                <select value={form.rpState || ""} onChange={(e) => onChange("rpState", e.target.value)} className={inputCls}>
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <Field label="Pincode" name="rpPincode" value={form.rpPincode} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <label className={`${labelCls} flex items-center gap-2 pt-5`}>
                <input type="checkbox" checked={!!form.rpAddressChanged} onChange={(e) => onChange("rpAddressChanged", e.target.checked)} />
                Address changed since last return
              </label>
            </div>

            <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Contact</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="STD Code" name="rpPhoneStdCode" value={form.rpPhoneStdCode} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Phone (Primary)" name="rpPhonePrimary" value={form.rpPhonePrimary} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Phone (Alternate)" name="rpPhoneAlternate" value={form.rpPhoneAlternate} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Fax" name="rpFax" value={form.rpFax} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Email (Primary)" name="rpEmailPrimary" value={form.rpEmailPrimary} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
              <Field label="Email (Alternate)" name="rpEmailAlternate" value={form.rpEmailAlternate} onChange={onChange} inputCls={inputCls} labelCls={labelCls} />
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-2 border-t pt-4">
          <button
            type="button"
            onClick={() => handleSave("all")}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-redhalo-red px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save for all quarters
          </button>
          {filingId && (
            <button
              type="button"
              onClick={() => handleSave("quarter")}
              disabled={saving}
              className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium ${
                darkMode ? "border-[#555]" : "border-gray-300"
              }`}
            >
              Save for this quarter only
            </button>
          )}
          <button
            type="button"
            onClick={() => onClose(false)}
            className="ml-auto text-xs text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, name, value, onChange, inputCls, labelCls, placeholder }) {
  return (
    <label className={labelCls}>
      {label}
      <input
        value={value || ""}
        onChange={(e) => onChange(name, e.target.value)}
        className={inputCls}
        placeholder={placeholder}
      />
    </label>
  );
}
